import { createContext, useContext, useMemo } from 'react';
import { useLocation, useMatches, UIMatch, RouteObject } from 'react-router';

import { IDetailsPage, IDetailsRoute, IDetailsSubRoutes } from './abstractions';
import { isActiveRoute, isSubRoute } from './utils';

// Internal type used for the React context for page-level navigation
interface NavigationContext<TData = undefined> {
  routes: (IDetailsRoute<TData> | IDetailsSubRoutes<TData>)[]
}

export interface NavigationRouteData<TData = undefined> {
  path: string;
  data: TData | undefined;
  isActive: boolean;
}

/**
 * Create a linked hook and route declaration function for a page with 'sub' navigation. (E.G. a details page)
 * 
 * This gives access to the total enumeration of _all_ the potential routes in the current view and see which one(s) are active.
 * This is a necessary wrapper over the standard react-router as there is no way of "listing" the available routes under a specific view nor
 * a clean way to determine if a specific relative path is "active". 
 * 
 * 
 * Note: It enforces that the layout and the sub-pages are asynchronously loaded.
 * 
 * Note: NavLink does _not_ work since it is a component which means the data is entirely wrapped up inside the virtual dom making it very difficult to determine if
 * any of the links inside of a section are active.
 * @returns 
 */
export function createNavigationContext<TData extends object = object>() {
  // We use this symbol to "mark" the react-router handle for the route match where this context is mounted. We do this so we can compute
  // the relative paths from the page-root to determine which page-routes are active.
  // E.G. if this is mounted as the /apps/:appId details page, then the handle will be on the match for that path so we can calculate if
  // 'details' is active when the current location is /apps/:appId/details.
  // See: https://reactrouter.com/start/framework/route-module#handle
  // TODO(nibeauli): this Symbol & match is repeated elsewhere, refactor into its own utility?
  const handleSymbol = Symbol('navigationContextHandle');
  function isSectionedNavigationMatch(match: UIMatch) {
    const handle = match.handle;
    return handle && typeof handle == "object" && handleSymbol in handle && handle[handleSymbol] === true;
  }
  const NavContext = createContext<NavigationContext<TData> | undefined>(undefined);

  /**
   * A hook for getting the list of possible page-level locations to navigate to.
   * 
   * This data will almost always be used to render a navigation bar
   * @returns the sections, with their associated route and if they are active.
   */
  function useNavigationRoutes() {
    const matches = useMatches();
    const match = matches.find(isSectionedNavigationMatch);
    const context = useContext(NavContext);
    const location = useLocation();

    const definition = context?.routes ?? [];
    const routesWithActive = useMemo(() => definition.map(route => ({
      path: route.path,
      data: route.data,
      isActive: isActiveRoute(location, route.path, match?.pathname),
    })), [location, context?.routes]);
    return routesWithActive;
  }

  // Create a set of routes within a page that can be navigated between by some form of navigation menu.
  // This has a more strongly typed API than the standard react-router handle for passing additional data into the routes themselves.
  function createRoutes(definition: IDetailsPage<TData>): RouteObject[] {
    const wrapperPromise = (async () => {
      const layout = await definition.layout();
      return (
        <NavContext.Provider value={{ routes: definition.routes }}>
          {layout}
        </NavContext.Provider>
      );
    })();

    const children = definition.routes.map(detailsRoute => {
      if (isSubRoute(detailsRoute)) {
        return {
          path: detailsRoute.path,
          children: detailsRoute.routes
        };
      } else {
        return {
          path: detailsRoute.path,
          lazy: async () => {
            const Page = await detailsRoute.page();
            return {
              hasErrorBoundary: false, //Unsure why typescript loses its mind if this isn't explicitly set.
              element: Page
            }
          },
        };
      }
    });

    return [
      {
        lazy: async () => {
          const navigationWrapper = await wrapperPromise;
          return {
            element: navigationWrapper,
            hasErrorBoundary: false,
          };
        },
        children,
        handle: {
          [handleSymbol]: true
        }
      }
    ];
  }

  return [useNavigationRoutes, createRoutes] as const;
}

export type UseNavigationRoutesHook<TData extends object = object> = ReturnType<typeof createNavigationContext<TData>>[0];

export type CreateRoutes<TData extends object = object> = ReturnType<typeof createNavigationContext<TData>>[0];
